/**
 * Conversation API Slice — schema-contracted against
 * @gearghost/api-schemas/conversation (tier B of the conversations
 * batch, 2026-07-21).
 *
 * Realtime: new messages and reactions arrive over Socket.IO and are
 * injected straight into the RTK Query cache (onCacheEntryAdded) — no
 * refetch. Every socket payload is validated against the SHARED schema
 * before it touches the cache; a drifted payload is dropped and logged,
 * never half-rendered.
 *
 * Two long-lived bugs died in this conversion:
 * - Conversation ids are cuid STRINGS. The old handler compared
 *   Number(id) !== Number(id) — NaN !== NaN — so every incoming socket
 *   message was discarded and web chat only updated on refetches.
 * - The list-preview patcher wrote `convo.lastMessage`, a field nothing
 *   renders. ConversationItem reads `conversation.messages[0]`; the
 *   patcher now updates the field the UI actually shows and bumps the
 *   conversation to the top (the server's updatedAt sort order).
 *
 * Pagination is CURSOR-based ({ nextCursor }), not page/pageSize. A
 * thread's messages are an infinite query: the pages load newest first
 * as the viewer scrolls up, and a refetch re-reads every loaded page from
 * the newest (see messagePagesQueryOptions in the shared schemas).
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CONVERSATION_ROUTES,
  CONVERSATION_SOCKET_EVENTS,
  ConversationsPageEnvelopeSchema,
  ConversationsPageSchema,
  unwrapConversationsPage,
  CreatedConversationEnvelopeSchema,
  CreatedConversationSchema,
  unwrapCreatedConversation,
  MessagesPageEnvelopeSchema,
  MessagesPageSchema,
  unwrapMessagesPage,
  MessageEnvelopeSchema,
  MessageSchema,
  unwrapMessage,
  MarkReadEnvelopeSchema,
  MarkReadResultSchema,
  unwrapMarkReadResult,
  SearchPageEnvelopeSchema,
  SearchPageSchema,
  unwrapSearchPage,
  ReactionEnvelopeSchema,
  ReactionSchema,
  unwrapReaction,
  LinkPreviewEnvelopeSchema,
  LinkPreviewSchema,
  unwrapLinkPreview,
  ReactionAddedPayloadSchema,
  ReactionRemovedPayloadSchema,
  messagePagesQueryOptions,
  addArrivedMessage,
  findLoadedMessage,
} from '@gearghost/api-schemas/conversation';
import type {
  ConversationsPage,
  CreatedConversation,
  MessagesPage,
  Message,
  MarkReadResult,
  SearchPage,
  Reaction,
  LinkPreview,
  CreateConversationRequest,
  SendMessageRequest,
  MarkReadRequest,
  ConversationsQueryRequest,
} from '@gearghost/api-schemas/conversation';
import { MessageOnlyEnvelopeSchema } from '@gearghost/api-schemas/auth';
import {
  SUPPORT_ROUTES,
  SupportUnreadEnvelopeSchema,
  SupportUnreadSchema,
  unwrapSupportUnread,
  CoverageIncreaseResultEnvelopeSchema,
  CoverageIncreaseResultSchema,
  unwrapCoverageIncreaseResult,
  SupportThreadEnvelopeSchema,
  SupportThreadSchema,
  unwrapSupportThread,
  type CoverageIncreaseRequest,
  type CoverageIncreaseResult,
  type SupportThread,
  type SupportUnread,
} from '@gearghost/api-schemas/support';
import { RECEIPT_ROUTES } from '@gearghost/api-schemas/receipts';
import axiosBaseQuery from './axiosBaseQuery';
import { onSchemaFailure } from './onSchemaFailure';
import { SOCKET_CONNECT, subscribeToSocket } from '@/redux/socketContext';
import { createLogger } from '@/utils/logger';
import { updateTotalUnreadMessages } from '@/redux/slices/socket';

const log = createLogger('conversationApi');

export const conversationApi = createApi({
  reducerPath: 'conversationApi',
  baseQuery: axiosBaseQuery(),
  onSchemaFailure,
  tagTypes: ['Conversations', 'Messages', 'SupportUnread'],
  endpoints: (builder) => ({
    // Get paginated conversation list (cursor-based)
    getConversations: builder.query<
      ConversationsPage,
      { cursor?: string; limit?: number } | void
    >({
      query: (args) => ({
        url: CONVERSATION_ROUTES.list,
        params: {
          ...(args?.cursor ? { cursor: args.cursor } : {}),
          ...(args?.limit ? { limit: args.limit } : {}),
          // The Messages page draws the support thread's row (Tyler,
          // 2026-10-09), so web asks for it among the rest.
          includeSupport: 'true',
        } satisfies ConversationsQueryRequest,
      }),
      rawResponseSchema: ConversationsPageEnvelopeSchema,
      transformResponse: unwrapConversationsPage,
      responseSchema: ConversationsPageSchema,
      providesTags: (result) =>
        result
          ? [
              ...result.conversations.map(({ id }) => ({
                type: 'Conversations' as const,
                id,
              })),
              { type: 'Conversations' as const, id: 'LIST' },
            ]
          : [{ type: 'Conversations' as const, id: 'LIST' }],
    }),

    // Create (or restore) a DIRECT conversation. Response participants
    // carry NO nested user objects — the list endpoint hydrates those.
    createConversation: builder.mutation<
      CreatedConversation,
      CreateConversationRequest
    >({
      query: (body) => ({
        url: CONVERSATION_ROUTES.create,
        method: 'POST',
        body,
      }),
      rawResponseSchema: CreatedConversationEnvelopeSchema,
      transformResponse: unwrapCreatedConversation,
      responseSchema: CreatedConversationSchema,
      invalidatesTags: [{ type: 'Conversations', id: 'LIST' }],
    }),

    // A conversation's messages, a page at a time: the first page is the
    // newest, each next page the one before it (the page param is the
    // cursor the last loaded page carried). The schemas run per page.
    // Socket events are handled via onCacheEntryAdded — new messages and
    // reactions are inserted directly into the cache without re-fetching.
    getMessages: builder.infiniteQuery<
      MessagesPage,
      { conversationId: string },
      string | null
    >({
      infiniteQueryOptions: messagePagesQueryOptions,
      query: ({ queryArg: { conversationId }, pageParam }) => ({
        url: CONVERSATION_ROUTES.messages(conversationId),
        params: pageParam ? { cursor: pageParam } : {},
      }),
      rawResponseSchema: MessagesPageEnvelopeSchema,
      transformResponse: unwrapMessagesPage,
      responseSchema: MessagesPageSchema,
      providesTags: (_, __, { conversationId }) => [
        { type: 'Messages', id: conversationId },
      ],
      async onCacheEntryAdded(
        { conversationId },
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch },
      ) {
        // Wait for initial query data to load
        await cacheDataLoaded;

        const handleMessageReceived = (raw: unknown) => {
          const parsed = MessageSchema.safeParse(raw);
          if (!parsed.success) {
            log.error(
              { issues: parsed.error.issues },
              'MESSAGE_RECEIVED payload failed MessageSchema — dropped',
            );
            return;
          }
          const message = parsed.data;

          // cuid ids — STRING equality. (Number(cuid) is NaN, and the old
          // NaN !== NaN comparison here discarded every incoming event.)
          if (message.conversationId !== conversationId) return;

          // Front of the newest page, unless a loaded page already holds it.
          updateCachedData((draft) => addArrivedMessage(draft, message));

          // Patch the conversation list in place instead of invalidating —
          // rapid invalidations from multiple sources can trip 429s. The
          // preview the UI renders is `conversation.messages[0]` (the old
          // patcher wrote a `lastMessage` field nothing reads), and the
          // list is server-sorted by updatedAt, so the freshest
          // conversation moves to the top.
          dispatch(
            conversationApi.util.updateQueryData(
              'getConversations',
              undefined,
              (draft) => {
                const idx = draft.conversations.findIndex(
                  (c) => c.id === message.conversationId,
                );
                const convo = draft.conversations[idx];
                if (!convo) return;
                convo.messages = [message];
                convo.updatedAt = message.createdAt;
                if (idx > 0) {
                  draft.conversations.splice(idx, 1);
                  draft.conversations.unshift(convo);
                }
              },
            ),
          );
        };

        const handleReactionAdded = (raw: unknown) => {
          const parsed = ReactionAddedPayloadSchema.safeParse(raw);
          if (!parsed.success) {
            log.error(
              { issues: parsed.error.issues },
              'REACTION_ADDED payload failed schema — dropped',
            );
            return;
          }
          const data = parsed.data;
          // The message may sit on any loaded page, or on none (a reaction
          // to a message older than the viewer has scrolled).
          updateCachedData((draft) => {
            findLoadedMessage(draft, data.messageId)?.reactions.push(data.reaction);
          });
        };

        const handleReactionRemoved = (raw: unknown) => {
          const parsed = ReactionRemovedPayloadSchema.safeParse(raw);
          if (!parsed.success) {
            log.error(
              { issues: parsed.error.issues },
              'REACTION_REMOVED payload failed schema — dropped',
            );
            return;
          }
          const data = parsed.data;
          updateCachedData((draft) => {
            const msg = findLoadedMessage(draft, data.messageId);
            if (msg) {
              msg.reactions = msg.reactions.filter(
                (r) => !(r.userId === data.userId && r.emoji === data.emoji),
              );
            }
          });
        };

        // After a reconnect the server-side room memberships are gone, so
        // any messages sent during the disconnect window were never
        // delivered via socket. Refetch to catch up: RTK re-reads every
        // loaded page from the newest, so a viewer scrolled back keeps
        // their place and still gets the missed messages. Listened on the
        // socket's connect, which fires on every reconnect; the manager's
        // 'reconnect' never fires for the one socketContext makes by hand
        // after a server disconnect, once the token is refreshed.
        const handleReconnect = () => {
          dispatch(
            conversationApi.util.invalidateTags([
              { type: 'Messages', id: conversationId },
              { type: 'Conversations', id: 'LIST' },
            ]),
          );
        };

        // Attached whenever a socket is or becomes available, a swap on
        // sign-in included (subscribeToSocket). It used to wait for one and
        // give up after five seconds, leaving a thread opened during a slow
        // sign-in with no listeners at all.
        const unsubscribe = subscribeToSocket((socket) => {
          socket.on(
            CONVERSATION_SOCKET_EVENTS.MESSAGE_RECEIVED,
            handleMessageReceived,
          );
          socket.on(
            CONVERSATION_SOCKET_EVENTS.REACTION_ADDED,
            handleReactionAdded,
          );
          socket.on(
            CONVERSATION_SOCKET_EVENTS.REACTION_REMOVED,
            handleReactionRemoved,
          );
          socket.on(SOCKET_CONNECT, handleReconnect);
          return () => {
            socket.off(
              CONVERSATION_SOCKET_EVENTS.MESSAGE_RECEIVED,
              handleMessageReceived,
            );
            socket.off(
              CONVERSATION_SOCKET_EVENTS.REACTION_ADDED,
              handleReactionAdded,
            );
            socket.off(
              CONVERSATION_SOCKET_EVENTS.REACTION_REMOVED,
              handleReactionRemoved,
            );
            socket.off(SOCKET_CONNECT, handleReconnect);
          };
        });

        // Clean up listeners when this cache entry is removed
        await cacheEntryRemoved;
        unsubscribe();
      },
    }),

    // Send a message. onQueryStarted inserts the sender's own message into
    // the cache from the API response, so the sender sees it even when the
    // socket is disconnected; the socket listener deduplicates by id.
    sendMessage: builder.mutation<
      Message,
      SendMessageRequest & { conversationId: string }
    >({
      query: ({ conversationId, ...body }) => ({
        url: CONVERSATION_ROUTES.send(conversationId),
        method: 'POST',
        body,
      }),
      rawResponseSchema: MessageEnvelopeSchema,
      transformResponse: unwrapMessage,
      responseSchema: MessageSchema,
      async onQueryStarted({ conversationId }, { dispatch, queryFulfilled }) {
        try {
          const { data: message } = await queryFulfilled;

          dispatch(
            conversationApi.util.updateQueryData(
              'getMessages',
              { conversationId },
              (draft) => addArrivedMessage(draft, message),
            ),
          );

          // Refresh conversations list for last-message preview
          dispatch(
            conversationApi.util.invalidateTags([
              { type: 'Conversations', id: 'LIST' },
            ]),
          );
        } catch {
          // Mutation failed — nothing to update
        }
      },
    }),

    // Mark a conversation as read. The response carries the DERIVED badge
    // total; the header badge slice updates from it directly.
    markConversationRead: builder.mutation<
      MarkReadResult,
      MarkReadRequest & { conversationId: string }
    >({
      query: ({ conversationId, ...body }) => ({
        url: CONVERSATION_ROUTES.markRead(conversationId),
        method: 'POST',
        body,
      }),
      rawResponseSchema: MarkReadEnvelopeSchema,
      transformResponse: unwrapMarkReadResult,
      responseSchema: MarkReadResultSchema,
      // SupportUnread rides along unconditionally (same rule as mobile):
      // the arg doesn't carry the conversation type, and refetching the
      // one-row support count after a non-support markRead is cheaper
      // than threading type here.
      invalidatesTags: (_, __, { conversationId }) => [
        { type: 'Conversations', id: conversationId },
        'SupportUnread',
      ],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(updateTotalUnreadMessages(data.totalUnreadMessages));
        } catch {
          // Mutation failed — nothing to update
        }
      },
    }),

    // Search messages across conversations (Postgres full-text; rows are
    // camelCase with a ts_headline `headline` carrying <b> markers).
    searchMessages: builder.query<
      SearchPage,
      { q: string; cursor?: string; limit?: number }
    >({
      query: ({ q, cursor, limit }) => ({
        url: CONVERSATION_ROUTES.search,
        params: {
          q,
          ...(cursor ? { cursor } : {}),
          ...(limit ? { limit } : {}),
        },
      }),
      rawResponseSchema: SearchPageEnvelopeSchema,
      transformResponse: unwrapSearchPage,
      responseSchema: SearchPageSchema,
    }),

    // Add a reaction. conversationId is SERVER-derived for the room
    // broadcast — the request carries only the emoji. No invalidatesTags:
    // the REACTION_ADDED socket event patches the cache directly.
    addReaction: builder.mutation<
      Reaction,
      { messageId: string; emoji: string }
    >({
      query: ({ messageId, emoji }) => ({
        url: CONVERSATION_ROUTES.addReaction(messageId),
        method: 'POST',
        body: { emoji },
      }),
      rawResponseSchema: ReactionEnvelopeSchema,
      transformResponse: unwrapReaction,
      responseSchema: ReactionSchema,
    }),

    // Remove a reaction. No invalidatesTags — REACTION_REMOVED patches the
    // cache. Response is the bare message-only envelope; no UI reads it.
    removeReaction: builder.mutation<
      void,
      { messageId: string; emoji: string }
    >({
      query: ({ messageId, emoji }) => ({
        url: CONVERSATION_ROUTES.removeReaction(messageId, emoji),
        method: 'DELETE',
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      // no responseSchema — UI reads only success; the envelope carries
      // no data payload.
      transformResponse: () => undefined,
    }),

    // Fetch link preview (OG metadata) for a URL. The server fails SOFT:
    // 200 with null data on any fetch error — hence the nullable schema.
    getLinkPreview: builder.query<LinkPreview | null, { url: string }>({
      query: ({ url }) => ({
        url: CONVERSATION_ROUTES.linkPreview,
        params: { url },
      }),
      rawResponseSchema: LinkPreviewEnvelopeSchema,
      transformResponse: unwrapLinkPreview,
      responseSchema: LinkPreviewSchema.nullable(),
      keepUnusedDataFor: 3600,
    }),

    // ── Support chat ────────────────────────────────────────────────
    // Moved from supportApi (2026-08-06) to mirror mobile: support chat
    // reads its messages through getMessages above (same endpoint, same
    // socket cache listener — supportApi used to carry a duplicate
    // messages query with a `/../conversations/` path hack), and the
    // same-slice arrangement lets sendSupportMessage insert into the
    // getMessages cache and markRead invalidate SupportUnread directly.

    // Get-or-create the current user's single support conversation with
    // an admin. Only the SEND differs from regular messaging.
    getOrCreateSupportConversation: builder.query<CreatedConversation, void>({
      query: () => SUPPORT_ROUTES.conversation,
      rawResponseSchema: CreatedConversationEnvelopeSchema,
      transformResponse: unwrapCreatedConversation,
      responseSchema: CreatedConversationSchema,
      providesTags: [{ type: 'Conversations', id: 'SUPPORT' }],
    }),

    // Send a support message. Distinct from sendMessage: this endpoint
    // also alerts admins (admin socket event + email) so support tickets
    // surface. Response is a normal Message — inserted into the
    // getMessages cache (dedup by id; the room socket may also deliver
    // it).
    // Body shape mirrors the sibling sendMessage above: the backend
    // validates BOTH against the same SendMessageRequestSchema (content
    // OR attachments), so the support widget stages attachments through
    // the identical contract the Messages page uses.
    sendSupportMessage: builder.mutation<
      Message,
      SendMessageRequest & { conversationId: string }
    >({
      query: ({ conversationId, ...body }) => ({
        url: SUPPORT_ROUTES.sendMessage(conversationId),
        method: 'POST',
        body,
      }),
      rawResponseSchema: MessageEnvelopeSchema,
      transformResponse: unwrapMessage,
      responseSchema: MessageSchema,
      async onQueryStarted({ conversationId }, { dispatch, queryFulfilled }) {
        try {
          const { data: message } = await queryFulfilled;

          dispatch(
            conversationApi.util.updateQueryData(
              'getMessages',
              { conversationId },
              (draft) => addArrivedMessage(draft, message),
            ),
          );
        } catch {
          // Mutation failed — nothing to insert
        }
      },
    }),

    // The widget-launcher badge. Read-only on the backend (never
    // get-or-creates a thread), so it's safe to keep subscribed while
    // the widget is closed. Live updates: markConversationRead above
    // invalidates on read, useUnreadCountSocketSync invalidates on the
    // UNREAD_COUNT socket event (admin replies).
    getSupportUnread: builder.query<SupportUnread, void>({
      query: () => SUPPORT_ROUTES.unread,
      rawResponseSchema: SupportUnreadEnvelopeSchema,
      transformResponse: unwrapSupportUnread,
      responseSchema: SupportUnreadSchema,
      providesTags: ['SupportUnread'],
    }),

    // The renter's coverage increase request (order changes, decision
    // 101): the backend composes the message (it knows the limit) into the
    // caller's support thread and answers the thread id. That thread's
    // messages are re-read so the widget that opens next shows it.
    requestCoverageIncrease: builder.mutation<CoverageIncreaseResult, CoverageIncreaseRequest>({
      query: (body) => ({
        url: SUPPORT_ROUTES.coverageIncrease,
        method: 'POST',
        body,
      }),
      rawResponseSchema: CoverageIncreaseResultEnvelopeSchema,
      transformResponse: unwrapCoverageIncreaseResult,
      responseSchema: CoverageIncreaseResultSchema,
      invalidatesTags: (result) =>
        result ? [{ type: 'Messages', id: result.conversationId }] : [],
    }),
    // A vendor's report that something on a receipt was read wrong
    // (receipts-redesign.md, Decision 13): the backend writes it into their
    // support thread, as the coverage increase request does, and answers the
    // thread, whose messages are re-read for the chat that opens next.
    reportReceiptMisread: builder.mutation<SupportThread, { receiptId: number }>({
      query: ({ receiptId }) => ({
        url: RECEIPT_ROUTES.misread(receiptId),
        method: 'POST',
      }),
      rawResponseSchema: SupportThreadEnvelopeSchema,
      transformResponse: unwrapSupportThread,
      responseSchema: SupportThreadSchema,
      invalidatesTags: (result) =>
        result ? [{ type: 'Messages', id: result.conversationId }] : [],
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useCreateConversationMutation,
  useGetMessagesInfiniteQuery,
  useSendMessageMutation,
  useMarkConversationReadMutation,
  useSearchMessagesQuery,
  useLazySearchMessagesQuery,
  useAddReactionMutation,
  useRemoveReactionMutation,
  useLazyGetLinkPreviewQuery,
  useGetOrCreateSupportConversationQuery,
  useSendSupportMessageMutation,
  useGetSupportUnreadQuery,
  useRequestCoverageIncreaseMutation,
  useReportReceiptMisreadMutation,
} = conversationApi;
